import { useState } from 'react';

export default function HomePage() {
  const [email, setEmail] = useState('');

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle email submission
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a2332] via-[#2a4a7c] to-[#3b82f6]"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        <div className="relative w-full max-w-[1400px] mx-auto px-8 py-20">
          <div className="flex items-center justify-between gap-16">
            {/* Left Content */}
            <div className="flex-1">
              <div className="mb-8">
                <img 
                  src="https://public.readdy.ai/ai/img_res/64ff68ae-a7bd-4453-8d1b-ccadfdfc73d0.png" 
                  alt="MERCES d.o.o. Logo" 
                  className="h-16 w-auto mb-6"
                />
                <h1 className="text-white text-7xl font-light mb-2">MERCES d.o.o.</h1>
              </div>
              
              <div className="space-y-3 mb-12">
                <p className="text-white text-3xl font-light">Expert iOS Development</p>
                <p className="text-white text-3xl font-medium pl-8">VPN & Cleaner Solutions</p>
                <p className="text-white text-3xl font-bold pl-16">Enhanced Mobile Security</p>
              </div>
              
              <p className="text-white/80 text-lg leading-relaxed max-w-xl">
                A team of experts developing modern applications for the iOS platform, designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>

            {/* Right Image */}
            <div className="flex-1 flex justify-center">
              <div className="relative transform rotate-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/9725a9d76e35e39d8848d32af285136c.jpg" 
                  alt="VPN App Interface"
                  className="w-full max-w-md rounded-3xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#F8F7F5]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex items-center gap-20">
            {/* Left Image */}
            <div className="w-[45%]">
              <div className="rounded-[40px] overflow-hidden shadow-xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20workspace%20with%20MacBooks%20and%20iOS%20devices%20on%20clean%20white%20desk%20shot%20from%20above%20with%20dramatic%20natural%20lighting%20professional%20tech%20office%20environment%20minimalist%20setup%20with%20coffee%20and%20notebooks%20high%20quality%20photography&width=800&height=900&seq=about-workspace-001&orientation=portrait" 
                  alt="Modern Workspace"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Content */}
            <div className="flex-1 pl-20">
              <div className="inline-block px-6 py-2 bg-[#E8F4F9] rounded-full mb-8">
                <span className="text-[#3b82f6] text-xs font-semibold tracking-widest uppercase">Who We Are</span>
              </div>
              
              <h2 className="text-6xl font-bold mb-4">
                <span className="text-[#1A1A1A] block">Modern Applications</span>
                <span className="text-[#6B6B6B] block">For iOS Platform</span>
              </h2>
              
              <p className="text-lg text-[#4A4A4A] leading-relaxed max-w-[520px]">
                MERCES d.o.o. is a team of dedicated experts specializing in developing cutting-edge applications for the iOS platform. Our flagship products, including VPN and Cleaner, are meticulously designed to enhance user experience and significantly improve the security of mobile devices. We combine technical excellence with user-centric design to deliver solutions that make a real difference in how people use their iOS devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-8">
          <h2 className="text-5xl font-bold text-white mb-20">Our Products</h2>
          
          <div className="grid grid-cols-3 gap-8">
            {/* VPN Card */}
            <div className="transform -rotate-2 transition-transform hover:rotate-0 hover:scale-105 duration-300 cursor-pointer">
              <div className="relative rounded-[32px] overflow-hidden aspect-[1/1.2] bg-gradient-to-br from-[#6B5CE7] to-[#4A90E2] p-8 flex flex-col items-center justify-center shadow-2xl">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10 text-center">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20VPN%20application%20interface%20with%20purple%20blue%20gradient%20theme%20displaying%20connection%20button%20and%20security%20shield%20icon%20modern%20app%20design%20clean%20UI%20professional%20render&width=400&height=500&seq=vpn-product-001&orientation=portrait" 
                      alt="VPN Application"
                      className="w-48 h-auto mx-auto opacity-90"
                    />
                  </div>
                  <h3 className="text-white text-3xl font-bold mb-3">VPN Application</h3>
                  <p className="text-white/90 text-base">Secure browsing and privacy protection</p>
                </div>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="transform rotate-2 transition-transform hover:rotate-0 hover:scale-105 duration-300 cursor-pointer">
              <div className="relative rounded-[32px] overflow-hidden aspect-[1/1.2] bg-gradient-to-br from-[#34D399] to-[#14B8A6] p-8 flex flex-col items-center justify-center shadow-2xl">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10 text-center">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20cleaner%20application%20interface%20with%20green%20teal%20gradient%20theme%20showing%20storage%20optimization%20and%20cleaning%20progress%20modern%20app%20UI%20design%20professional%20quality%20render&width=400&height=500&seq=cleaner-product-001&orientation=portrait" 
                      alt="Cleaner Application"
                      className="w-48 h-auto mx-auto opacity-90"
                    />
                  </div>
                  <h3 className="text-white text-3xl font-bold mb-3">Cleaner Tool</h3>
                  <p className="text-white/90 text-base">Optimize device performance effortlessly</p>
                </div>
              </div>
            </div>

            {/* Security Card */}
            <div className="transform -rotate-1 transition-transform hover:rotate-0 hover:scale-105 duration-300 cursor-pointer">
              <div className="relative rounded-[32px] overflow-hidden aspect-[1/1.2] bg-gradient-to-br from-[#F97316] to-[#EF4444] p-8 flex flex-col items-center justify-center shadow-2xl">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10 text-center">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20security%20features%20application%20with%20orange%20red%20gradient%20theme%20displaying%20shield%20protection%20and%20security%20status%20modern%20iOS%20app%20interface%20professional%20render&width=400&height=500&seq=security-product-001&orientation=portrait" 
                      alt="Security Features"
                      className="w-48 h-auto mx-auto opacity-90"
                    />
                  </div>
                  <h3 className="text-white text-3xl font-bold mb-3">Security Suite</h3>
                  <p className="text-white/90 text-base">Comprehensive mobile device protection</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-[#F9FAFB]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex flex-col items-center">
            <div className="w-full max-w-[1200px] h-[600px] rounded-[28px] overflow-hidden mb-16 shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=multiple%20iOS%20devices%20including%20iPhones%20and%20iPads%20displaying%20VPN%20and%20cleaner%20applications%20arranged%20on%20clean%20white%20surface%20with%20dramatic%20side%20lighting%20creating%20long%20shadows%20professional%20product%20photography%20with%20film%20grain%20texture%20high%20quality&width=1200&height=600&seq=cta-hero-001&orientation=landscape" 
                alt="iOS Devices Showcase"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="text-center max-w-3xl">
              <h2 className="text-4xl font-bold leading-relaxed mb-14">
                <span className="text-[#111827]">Experience the difference</span><br />
                <span className="text-[#111827]">with </span>
                <span className="text-[#9CA3AF] italic">professional iOS solutions</span><br />
                <span className="text-[#111827]">designed for modern users</span>
              </h2>

              <div className="inline-flex items-center justify-center px-12 py-5 bg-[#111827] rounded-full">
                <span className="text-white text-lg font-medium whitespace-nowrap">Available on App Store</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#1E3A2F] py-20">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-4 gap-10 mb-20">
            {/* Column 1 - Brand */}
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/64ff68ae-a7bd-4453-8d1b-ccadfdfc73d0.png" 
                alt="MERCES d.o.o." 
                className="h-12 w-auto mb-6 brightness-0 invert"
              />
              <div className="space-y-1.5">
                <p className="text-[#A8B5AF] text-sm">Expert iOS Development</p>
                <p className="text-[#A8B5AF] text-sm">VPN & Cleaner Products</p>
                <p className="text-[#A8B5AF] text-sm">Enhancing Mobile Experience</p>
              </div>
            </div>

            {/* Column 2 - Newsletter */}
            <div>
              <h3 className="text-[#D1D5DB] text-xs font-semibold tracking-wider uppercase mb-6">Stay Updated</h3>
              <form onSubmit={handleEmailSubmit} className="mb-3">
                <div className="relative">
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full bg-transparent border-b border-[#4B5563] text-white text-sm py-2 pr-10 focus:outline-none focus:border-[#14B8A6] transition-colors"
                  />
                  <button 
                    type="submit"
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded transition-colors cursor-pointer"
                    aria-label="Submit email"
                  >
                    <i className="ri-arrow-right-line text-[#D1D5DB]"></i>
                  </button>
                </div>
              </form>
              <p className="text-[#9CA3AF] text-xs">We respect your privacy</p>
            </div>

            {/* Column 3 - Products */}
            <div>
              <h3 className="text-[#D1D5DB] text-xs font-semibold tracking-wider uppercase mb-6">Products</h3>
              <div className="space-y-5">
                <p className="text-white text-[15px]">VPN Application</p>
                <p className="text-white text-[15px]">Cleaner Tool</p>
                <p className="text-white text-[15px]">Security Suite</p>
              </div>
            </div>

            {/* Column 4 - Company */}
            <div>
              <h3 className="text-[#D1D5DB] text-xs font-semibold tracking-wider uppercase mb-6">Company</h3>
              <div className="space-y-5">
                <p className="text-white text-[15px]">About Us</p>
                <p className="text-white text-[15px]">Our Team</p>
                <p className="text-white text-[15px]">Careers</p>
              </div>
            </div>
          </div>

          {/* Contact Information Block */}
          <div className="bg-[#172820] rounded-2xl p-12 mb-12">
            <div className="flex items-center justify-between flex-wrap gap-8">
              <div className="flex items-center gap-8 flex-wrap">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-[#D1D5DB] text-base"></i>
                  <span className="text-[#D1D5DB] text-sm">Address: Obala kralja Tomislava 14</span>
                </div>
                <div className="w-px h-6 bg-[#4B5563]"></div>
                <span className="text-[#D1D5DB] text-sm">City: Makarska</span>
                <div className="w-px h-6 bg-[#4B5563]"></div>
                <span className="text-[#D1D5DB] text-sm">Zip: 21300</span>
                <div className="w-px h-6 bg-[#4B5563]"></div>
                <span className="text-[#D1D5DB] text-sm">Country: Croatia</span>
              </div>
              
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line text-[#D1D5DB] text-base"></i>
                  <span className="text-[#D1D5DB] text-sm">Email: contact@mercesdoo.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line text-[#D1D5DB] text-base"></i>
                  <span className="text-[#D1D5DB] text-sm">Phone: +1 5705651215</span>
                </div>
              </div>
            </div>
          </div>

          {/* Large Typography */}
          <div className="text-center overflow-hidden">
            <h2 className="text-[#E8DCC8] text-[180px] font-black tracking-tighter leading-none">
              MERCES
            </h2>
          </div>
        </div>
      </section>
    </div>
  );
}
